import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { getMarketStatus } from '@utils/marketStatus';
import { useState } from 'react';
import { BOLD_SX, SECONDARY_TEXT_SX } from '../styles';
import { useTradingHub } from '../tradingHubContext';
import { formatMoney } from '../utils';

const CommandStrip = () => {
  const { sys } = useThemeTokens();
  const {
    statusBarStats,
    triggerRefresh,
    insightSidebarOpen,
    setInsightSidebarOpen,
  } = useTradingHub();
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const marketStatus = getMarketStatus(
    sys as unknown as { color: Record<string, string> },
    new Date(),
  );

  const handleRefresh = () => {
    triggerRefresh();
    setLastRefreshed(new Date());
  };

  const statusItems = [
    statusBarStats.needsAction > 0 && {
      color: sys.color.negative,
      text: `${statusBarStats.needsAction} need attention`,
    },
    statusBarStats.activeOperations > 0 && {
      color: sys.color.warning,
      text: `${statusBarStats.activeOperations} ops active`,
    },
    statusBarStats.tradesInFlight > 0 && {
      color: sys.color.primary,
      text: `${statusBarStats.tradesInFlight} trades in flight`,
    },
    statusBarStats.onTrack > 0 &&
      statusBarStats.needsAction === 0 &&
      statusBarStats.activeOperations === 0 && {
        color: sys.color.positive,
        text: `${statusBarStats.onTrack} on track`,
      },
  ].filter(Boolean) as { color: string; text: string }[];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 0.75,
        bgcolor: sys.color.surface,
        borderBottom: '2px solid',
        borderColor: sys.color.outlineVariant,
        minHeight: 40,
        flexShrink: 0,
      }}
    >
      <Box display='flex' alignItems='center' gap={3}>
        <Box display='flex' alignItems='center' gap={0.75}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: marketStatus.dot,
              animation:
                marketStatus.key === 'open' ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }}
          />
          <Typography
            variant='labelSmall'
            sx={{
              color: marketStatus.color,
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            {marketStatus.label}
          </Typography>
        </Box>
        <Box
          sx={{ width: '1px', height: 20, bgcolor: sys.color.outlineVariant }}
        />
        {statusBarStats.totalAum > 0 && (
          <Box display='flex' alignItems='center' gap={0.5}>
            <Typography
              variant='labelSmall'
              sx={{
                ...SECONDARY_TEXT_SX,
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              AUM
            </Typography>
            <Typography variant='bodySmall' sx={BOLD_SX}>
              {formatMoney(statusBarStats.totalAum, { compact: true })}
            </Typography>
          </Box>
        )}
        <Box
          sx={{ width: '1px', height: 20, bgcolor: sys.color.outlineVariant }}
        />
        {statusItems.map((item) => (
          <Box key={item.text} display='flex' alignItems='center' gap={0.5}>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: item.color,
              }}
            />
            <Typography
              variant='labelSmall'
              sx={{ color: item.color, fontWeight: 600 }}
            >
              {item.text}
            </Typography>
          </Box>
        ))}
        {statusBarStats.needsAction === 0 &&
          statusBarStats.onTrack === 0 &&
          statusBarStats.totalAum === 0 && (
            <Typography
              variant='labelSmall'
              sx={{ ...SECONDARY_TEXT_SX, fontStyle: 'italic' }}
            >
              Loading portfolio data...
            </Typography>
          )}
      </Box>
      <Box display='flex' alignItems='center' gap={1.5}>
        <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
          {lastRefreshed.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })}
        </Typography>
        <IconButton
          size='small'
          onClick={() => setInsightSidebarOpen(!insightSidebarOpen)}
          label='Toggle Insights'
        >
          <LightbulbIcon
            sx={{
              fontSize: 16,
              color: insightSidebarOpen
                ? sys.color.warning
                : sys.color.onSurfaceVariant,
            }}
          />
        </IconButton>
        <IconButton size='small' onClick={handleRefresh} label='Refresh'>
          <RefreshIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CommandStrip;
